import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { ListPlus, Plus, Edit, Trash } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import Chip from '@/components/ui/Chip.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { tourConsentsConfigRoute } from '@/features/dashboard/tourConsents/routes/tourConsentsConfigRoute.ts';
import useGetTourConsents from '@/features/dashboard/tourConsents/hooks/useGetTourConsents.ts';
import useRemoveTourConsent from '@/features/dashboard/tourConsents/hooks/useRemoveTourConsent.ts';
import { TourConsentDto } from '@/features/dashboard/tourConsents/types/TourConsentDto.ts';
import TourConsentAddModal from '@/features/dashboard/tourConsents/components/TourConsentAddModal.tsx';
import TourConsentEditModal from '@/features/dashboard/tourConsents/components/TourConsentEditModal.tsx';
import useGetConsentTemplates from '@/features/dashboard/consentTemplates/hooks/useGetConsentTemplates.ts';
import { getConsentTypeLabel } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.utils.ts';

const TourConsentsConfigCard = () => {
  const { tourId } = tourConsentsConfigRoute.useParams();
  const { data: tourConsents } = useGetTourConsents(tourId);
  const { data: consentTemplates } = useGetConsentTemplates();

  const getConsentType = (consentTemplateId: string): string | undefined => {
    const template = consentTemplates?.find((t) => t.id === consentTemplateId);
    return template ? getConsentTypeLabel(template.type) : undefined;
  };
  const { confirmDeleteWithCallback } = confirmDialogUseHook();
  const { mutate } = useRemoveTourConsent(tourId);
  const [editTourConsentDto, setEditTourConsentDto] = useState<
    TourConsentDto | undefined
  >(undefined);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleRemoveConsent = (index: number) => {
    const consentId = tourConsents?.[index]?.id;
    if (!consentId) return;

    confirmDeleteWithCallback({
      title: 'Usuń zgodę',
      description: 'Czy na pewno chcesz usunąć tę zgodę?',
      confirmText: 'Usuń zgodę',
      loadingText: 'Usuwanie...',
      onConfirm: () => {
        mutate(consentId);
      },
    });
  };

  const openAddModal = () => {
    setEditTourConsentDto(undefined);
    setIsAddModalOpen(true);
  };

  const openEditModal = (index: number) => {
    const consent = tourConsents?.[index];
    if (!consent) return;
    setEditTourConsentDto(consent);
  };

  return (
    <Card>
      <CardHeader className="border-b p-4">
        <CardTitle>Zgody</CardTitle>
        <CardDescription>
          Dodaj zgody, które uczestnicy muszą zaakceptować podczas rejestracji.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isAddModalOpen && (
          <TourConsentAddModal onClose={() => setIsAddModalOpen(false)} />
        )}

        {editTourConsentDto !== undefined && (
          <TourConsentEditModal
            onClose={() => setEditTourConsentDto(undefined)}
            tourConsentDto={editTourConsentDto}
          />
        )}

        {!tourConsents || tourConsents?.length === 0 ? (
          <NoConsentsPlaceholder openModal={openAddModal} />
        ) : (
          <>
            <CardContent className="flex flex-col gap-1 bg-neutral-50 py-2">
              {tourConsents?.map((consent, index) => (
                <TourConsentSummary
                  key={index}
                  consent={consent}
                  consentType={getConsentType(consent.consentTemplateId)}
                  onRemove={() => handleRemoveConsent(index)}
                  onEdit={() => openEditModal(index)}
                />
              ))}
            </CardContent>

            <CardContent className="py-2">
              <ActionButton tone="outline" size="sm" onClick={openAddModal}>
                <Plus />
                Dodaj zgodę
              </ActionButton>
            </CardContent>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const TourConsentSummary = ({
  consent,
  consentType,
  onEdit,
  onRemove,
}: {
  consent: TourConsentDto;
  consentType?: string;
  onEdit?: () => void;
  onRemove?: () => void;
}) => {
  return (
    <Card className="flex flex-row items-center justify-between bg-basic-white px-3 py-1.5">
      <div className="flex flex-row items-center gap-2">
        <div className="text-sm font-semibold text-base-text">
          {consent.displayName}
        </div>
        <div className="flex flex-row items-center gap-1">
          {consentType && (
            <Chip label={consentType} tone="accent" fill="soft" />
          )}
          {consent.required && (
            <Chip label="Wymagana" tone="neutral" fill="soft" />
          )}
        </div>
      </div>
      <div className="flex flex-row items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-700"
            title="Edytuj zgodę"
          >
            <Edit className="size-4" />
          </button>
          <button
            onClick={onRemove}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-red-50 hover:text-red-600"
            title="Usuń zgodę"
          >
            <Trash className="size-4" />
          </button>
        </div>
      </div>
    </Card>
  );
};

const NoConsentsPlaceholder = ({ openModal }: { openModal: () => void }) => {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-b-[13px] bg-neutral-50 p-4">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-[60px] w-[60px] items-center justify-center rounded-md bg-basic-white">
          <ListPlus size={24} className="text-neutral-400" />
        </div>
        <div className="space-y-1 text-center">
          <p className="font-medium text-base-text">Brak zgód</p>
          <p className="text-sm text-neutral-600">Dodaj pierwszą zgodę</p>
        </div>
      </div>
      <ActionButton tone="outline" size="sm" onClick={openModal}>
        <Plus />
        Dodaj zgodę
      </ActionButton>
    </div>
  );
};

export default TourConsentsConfigCard;
